import React from 'react';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation, EffectFade } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import { mediaUrl } from '../../api/client';
import { IconChevronLeft, IconChevronRight } from '../common/Icons';

const posClass = (pos = 'middle-center') => `sf__slide-block--${pos}`;

function SlideContent({ slide, mobile = false }) {
  const hasText = slide.heading || slide.subheading || slide.buttonText;
  if (!hasText) return null;
  const align = (slide.position || 'middle-center').split('-')[1];
  const textAlign = align === 'left' ? 'text-left' : align === 'right' ? 'text-right' : 'text-center';
  return (
    <div className={`sf__slide-content ${mobile ? 'sf__slide-content--mobile w-full text-center px-4' : `max-w-4xl w-max ${textAlign}`}`} style={{ color: slide.textColor || '#000' }}>
      {slide.subheading && <p className={`slide__block-subheading mb-2 ${mobile ? 'text-base' : 'text-lg'}`}>{slide.subheading}</p>}
      {slide.heading && (
        <h2 className={mobile ? 'sf__mobile-heading text-4xl leading-snug mb-3' : 'slide__block-title text-5xl mb-5 lg:leading-tight xl:leading-tight 2xl:leading-tight'} style={{ color: slide.textColor || '#000' }}>{slide.heading}</h2>
      )}
      {slide.description && <div className={`slide__block-description mb-5 ${mobile ? 'text-sm' : 'text-base'}`} dangerouslySetInnerHTML={{ __html: slide.description }} />}
      {slide.buttonText && (
        <Link to={slide.link || '#'} className={`inline-block sf__btn sf__btn-primary ${mobile ? 'sf__mobile-button sf__btn-medium' : 'slide__block-link'}`}>{slide.buttonText}</Link>
      )}
    </div>
  );
}

// Full-width hero slideshow: background image on desktop, stacked image + text on mobile (like the reference theme).
export default function HeroSlider({ section }) {
  const d = section.data || {};
  const slides = (d.slides || []).filter((s) => s.image);
  if (!slides.length) return null;
  const autoplay = Number(d.autoplay || 0);
  const id = `hero-${section.id}`;

  const ratio = Number(d.aspectRatio) || 2.1333;
  return (
    <section className={`relative home-banner__slideshow ${d.fullWidth === false ? 'container-fluid' : ''}`} style={{ '--data-autoplay-speed': `${autoplay}s`, '--min-aspect-ratio': ratio, '--slide-height': `${(100 / ratio).toFixed(3)}vw` }}>
      <div className="w-full">
        <Swiper
          modules={[Autoplay, Pagination, Navigation, EffectFade]}
          loop={slides.length > 1}
          effect={d.effect === 'fade' ? 'fade' : 'slide'}
          autoplay={autoplay ? { delay: autoplay * 1000, disableOnInteraction: false } : false}
          pagination={{ el: `.${id}-pag`, clickable: true }}
          navigation={{ prevEl: `.${id}-prev`, nextEl: `.${id}-next` }}
          wrapperClass="swiper-wrapper sf__slider sf__slider--adapt"
          className="swiper-container swiper-container-initialized"
        >
          {slides.map((s, i) => (
            <SwiperSlide key={i} className={`relative sf-slider--has-link ${i === 0 ? 'sf-slider--first' : ''}`}>
              {/* Desktop */}
              <div className="hidden position md:block md:h-full bg-cover bg-center relative">
                {s.link && <Link to={s.link} className="sf-slider__link absolute inset-0 z-[1]" aria-label={s.heading || 'View'} />}
                <div className="sf__slide-bg bg-cover bg-no-repeat bg-center" style={{ backgroundImage: `url(${mediaUrl(s.image)})`, backgroundPosition: 'center' }} />
                <div className={`sf__slide-block slide__block-desktop flex ${posClass(s.position)} container-fluid w-full h-full py-24 absolute inset-0 z-[2] pointer-events-none`} style={{ '--btn-color': '#000', '--btn-color-hover': '#FFF' }}>
                  <div className="pointer-events-auto"><SlideContent slide={s} /></div>
                </div>
              </div>
              {/* Mobile */}
              <div className="md:hidden block pb-6">
                <div className="sf__slide-image relative">
                  <Link to={s.link || '#'} className="sf-slider__link block">
                    <div className="sf-image" style={s.mobileImage ? undefined : { '--aspect-ratio': ratio }}>
                      <img src={mediaUrl(s.mobileImage || s.image)} alt={s.heading || s.alt || ''} loading={i === 0 ? 'eager' : 'lazy'} />
                    </div>
                  </Link>
                </div>
                {(s.heading || s.buttonText) && (
                  <div className="sf__slide-block eg-mobile-block flex container-fluid w-full py-6 px-6 pb-0 relative z-10 justify-center">
                    <SlideContent slide={s} mobile />
                  </div>
                )}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        {slides.length > 1 && (
          <>
            <div className="sf-slider__controls flex items-center justify-center mt-5 md:mt-0 -mx-2 sf-slider__controls--absolute sf-slider__controls--full hidden md:flex" style={{ '--nav-offset': '25px' }}>
              <button type="button" className={`sf-slider__controls-button sf-slider__controls-prev border rounded-full ${id}-prev`} aria-label="Previous slide"><IconChevronLeft width={16} height={16} /></button>
              <button type="button" className={`sf-slider__controls-button sf-slider__controls-next border rounded-full ${id}-next`} aria-label="Next slide"><IconChevronRight width={16} height={16} /></button>
            </div>
            <div className={`swiper-pagination w-full sm:mt-6 sf__dot-light ${id}-pag eg-hero-pag`} />
          </>
        )}
      </div>
    </section>
  );
}
